import React from "react";
import CheckCircleIcon from "./icons/CheckCircleIcon";
import AlertTriangleIcon from "./icons/AlertTriangleIcon";
import CloseIcon from "./icons/CloseIcon";
import { useTranslation } from "react-i18next";

// Local minimal notification shape (decoupled from any legacy hook)
interface BasicNotification {
  message: string;
  type?: string;
  action?: { label: string; onClick: () => void };
}

interface ToastProps {
  notification: BasicNotification;
  onClose: () => void;
}

const toastStyles: Record<
  string,
  { bg: string; iconColor: string; icon: React.ReactNode }
> = {
  success: {
    bg: "bg-green-500/20 border border-green-500/40 backdrop-blur-xl",
    iconColor: "text-green-400",
    icon: <CheckCircleIcon className="h-6 w-6" />,
  },
  error: {
    bg: "bg-rose-500/20 border border-rose-500/40 backdrop-blur-xl",
    iconColor: "text-rose-400",
    icon: <AlertTriangleIcon className="h-6 w-6" />,
  },
  info: {
    bg: "bg-blue-500/20 border border-blue-500/40 backdrop-blur-xl",
    iconColor: "text-blue-400",
    icon: <AlertTriangleIcon className="h-6 w-6" />,
  },
  warning: {
    bg: "bg-yellow-500/20 border border-yellow-500/40 backdrop-blur-xl",
    iconColor: "text-yellow-400",
    icon: <AlertTriangleIcon className="h-6 w-6" />,
  },
};

const Toast: React.FC<ToastProps> = ({ notification, onClose }) => {
  const { t } = useTranslation("common");
  let { message, type = "info", action } = notification;
  if (!message || (typeof message === "string" && message.trim() === "")) {
    message = "…"; // fallback placeholder to avoid blank toast
  }
  const styles = toastStyles[type] || toastStyles["info"];
  return (
    <div
      className={`flex items-center p-4 rounded-lg shadow-lg text-white ${styles.bg} animate-fade-in-right`}
      role="alert"
    >
      <div className={`flex-shrink-0 ${styles.iconColor}`}>{styles.icon}</div>
      <div className="ms-3 text-sm font-medium flex-grow">{message}</div>
      {action && (
        <button
          type="button"
          onClick={action.onClick}
          className="ms-3 flex-shrink-0 rounded-md border border-white/20 bg-white/10 px-2 py-1 text-xs font-semibold text-white transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
        >
          {action.label}
        </button>
      )}
      <button
        onClick={onClose}
        className="ms-auto -mx-1.5 -my-1.5 bg-white/10 text-white rounded-lg focus:ring-2 focus:ring-white/20 p-1.5 hover:bg-white/20 inline-flex h-8 w-8"
        aria-label={t("actions.close")}
      >
        <span className="sr-only">{t("actions.close")}</span>
        <CloseIcon className="w-5 h-5" />
      </button>
    </div>
  );
};

export default Toast;
